import React, { useState, useEffect } from 'react';
import { format, subDays } from 'date-fns';
import { comprehensiveHRService } from '../../../lib/checkInAttendanceService';
import Icon from '../../../components/AppIcon';


const TrainerMetrics = ({ hrProfiles, loading, gymId }) => {
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [trainerMetrics, setTrainerMetrics] = useState([]);
  const [trainerSessions, setTrainerSessions] = useState([]);
  const [dateRange, setDateRange] = useState('7days');
  const [metricsLoading, setMetricsLoading] = useState(false);

  const trainers = hrProfiles?.filter((p) => p?.staff?.role === 'trainer');

  useEffect(() => {
    if (selectedTrainer) {
      loadTrainerData();
    }
  }, [selectedTrainer, dateRange]);

  const loadTrainerData = async () => {
    if (!selectedTrainer) return;
    setMetricsLoading(true);

    const endDate = format(new Date(), 'yyyy-MM-dd');
    const startDate = format(
      subDays(new Date(), dateRange === '7days' ? 7 : dateRange === '30days' ? 30 : 90),
      'yyyy-MM-dd'
    );

    const [metricsRes, sessionsRes] = await Promise.all([
      comprehensiveHRService?.getTrainerMetrics(selectedTrainer, startDate, endDate),
      comprehensiveHRService?.getTrainerSessions(selectedTrainer, startDate, endDate)
    ]);

    if (metricsRes?.data) setTrainerMetrics(metricsRes?.data);
    if (sessionsRes?.data) setTrainerSessions(sessionsRes?.data);

    setMetricsLoading(false);
  };

  const calculateTotals = () => {
    const totalSessions = trainerMetrics?.reduce((sum, m) => sum + (m?.sessions_completed || 0), 0);
    const totalRevenue = trainerMetrics?.reduce((sum, m) => sum + (parseFloat(m?.total_revenue) || 0), 0);
    const totalCommission = trainerMetrics?.reduce((sum, m) => sum + (parseFloat(m?.total_commission) || 0), 0);
    const avgRating = trainerMetrics?.length > 0
      ? trainerMetrics?.reduce((sum, m) => sum + (parseFloat(m?.average_session_rating) || 0), 0) / trainerMetrics?.length
      : 0;
    const avgRetention = trainerMetrics?.length > 0
      ? trainerMetrics?.reduce((sum, m) => sum + (parseFloat(m?.client_retention_rate) || 0), 0) / trainerMetrics?.length
      : 0;

    return { totalSessions, totalRevenue, totalCommission, avgRating, avgRetention };
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${parseFloat(amount)?.toFixed(2)}`;
  };

  const totals = selectedTrainer ? calculateTotals() : null;

  return (
    <div className="space-y-6">
      {/* Trainer Selection */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div className="flex-1">
            <label className="block text-sm font-medium text-foreground mb-2">
              Select Trainer
            </label>
            <select
              value={selectedTrainer || ''}
              onChange={(e) => setSelectedTrainer(e?.target?.value || null)}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Choose a trainer...</option>
              {trainers?.map((trainer) => (
                <option key={trainer?.staff_id} value={trainer?.staff_id}>
                  {trainer?.staff?.full_name} - {trainer?.position}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Date Range
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e?.target?.value)}
              className="px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
            </select>
          </div>
        </div>
      </div>

      {!selectedTrainer ? (
        <div className="card text-center py-12">
          <Icon name="TrendingUp" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">Select a Trainer</h3>
          <p className="text-muted-foreground">Choose a trainer to view their performance metrics</p>
        </div>
      ) : metricsLoading ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Metrics Summary */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="card">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-primary/10">
                  <Icon name="CheckCircle" size={24} className="text-primary" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">{totals?.totalSessions}</div>
                  <div className="text-xs text-muted-foreground">Sessions Completed</div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-success/10">
                  <Icon name="DollarSign" size={24} className="text-success" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">{formatCurrency(totals?.totalRevenue)}</div>
                  <div className="text-xs text-muted-foreground">Revenue Generated</div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-primary/10">
                  <Icon name="TrendingUp" size={24} className="text-primary" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">{formatCurrency(totals?.totalCommission)}</div>
                  <div className="text-xs text-muted-foreground">Total Commission</div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-warning/10">
                  <Icon name="Star" size={24} className="text-warning" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">{totals?.avgRating?.toFixed(1)}</div>
                  <div className="text-xs text-muted-foreground">Avg Rating</div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-success/10">
                  <Icon name="Users" size={24} className="text-success" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">{totals?.avgRetention?.toFixed(1)}%</div>
                  <div className="text-xs text-muted-foreground">Client Retention</div>
                </div>
              </div>
            </div>
          </div>

          {/* Daily Metrics Table */}
          <div className="card">
            <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="BarChart3" size={20} className="text-primary" />
              Daily Performance Metrics
            </h3>
            {trainerMetrics?.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No metrics data available</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Sessions</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Cancelled</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Revenue</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Commission</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Rating</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Retention</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trainerMetrics?.map((metric) => (
                      <tr key={metric?.id} className="border-b border-border hover:bg-muted/30">
                        <td className="py-3 px-4 text-sm text-foreground">
                          {format(new Date(metric?.metric_date), 'MMM d, yyyy')}
                        </td>
                        <td className="py-3 px-4 text-sm text-center text-foreground font-medium">
                          {metric?.sessions_completed}
                        </td>
                        <td className="py-3 px-4 text-sm text-center text-error">
                          {metric?.sessions_cancelled}
                        </td>
                        <td className="py-3 px-4 text-sm text-right text-success font-medium">
                          {formatCurrency(metric?.total_revenue)}
                        </td>
                        <td className="py-3 px-4 text-sm text-right text-primary font-medium">
                          {formatCurrency(metric?.total_commission)}
                        </td>
                        <td className="py-3 px-4 text-sm text-center text-foreground">
                          {metric?.average_session_rating ? (
                            <span className="flex items-center justify-center gap-1">
                              <Icon name="Star" size={14} className="text-warning" />
                              {metric?.average_session_rating?.toFixed(1)}
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-center text-foreground">
                          {metric?.client_retention_rate ? `${metric?.client_retention_rate?.toFixed(1)}%` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Recent Sessions */}
          <div className="card">
            <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Calendar" size={20} className="text-primary" />
              Recent Sessions
            </h3>
            {trainerSessions?.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No sessions found</p>
            ) : (
              <div className="space-y-3">
                {trainerSessions?.slice(0, 10)?.map((session) => (
                  <div key={session?.id} className="p-4 rounded-lg bg-muted/30">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="font-semibold text-foreground">{session?.session_type}</div>
                        <div className="text-sm text-muted-foreground">
                          {session?.member?.full_name || 'Group Session'}
                        </div>
                      </div>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          session?.status === 'completed'
                            ? 'bg-success/10 text-success'
                            : session?.status === 'scheduled' ?'bg-warning/10 text-warning' :'bg-error/10 text-error'
                        }`}
                      >
                        {session?.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <div className="text-muted-foreground">Date</div>
                        <div className="font-medium text-foreground">
                          {format(new Date(session?.session_date), 'MMM d, yyyy')}
                        </div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Time</div>
                        <div className="font-medium text-foreground">
                          {session?.start_time} - {session?.end_time}
                        </div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Revenue</div>
                        <div className="font-medium text-success">
                          {formatCurrency(session?.revenue_generated)}
                        </div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Commission</div>
                        <div className="font-medium text-primary">
                          {formatCurrency(session?.commission_amount)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TrainerMetrics;